import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { CigarVertical } from "@/components/Illustrations";
import ChapterRow, { ChapterCta, ManuscriptNote, ChapterBridge } from "./ChapterRow";

/**
 * Chapter IV — THE COLLECTION
 * Question: What blends? What personalities?
 */
export default function ChapterCollection() {
  const { t, lang } = useI18n();
  const items = t("blends.items") || [];
  const [active, setActive] = useState(0);
  const cur = items[active] || items[0] || {};

  const epigraphEN = (
    <>
      <span className="text-tobacco/85">What lives in the library?</span>
      <br />Four blends. Four temperaments.
    </>
  );
  const epigraphES = (
    <>
      <span className="text-tobacco/85">¿Qué habita en la biblioteca?</span>
      <br />Cuatro ligas. Cuatro temperamentos.
    </>
  );

  return (
    <ChapterRow
      id="collection"
      testid="chapter-collection"
      numeral="V"
      name="THE FINAL CIGAR"
      chapterLabel={lang === "es" ? "Capítulo V — El Cigarro" : "Chapter V — The Final Cigar"}
      epigraph={lang === "es" ? epigraphES : epigraphEN}
      headline={t("blends.title")}
      body={
        <>
          <p>
            {lang === "es"
              ? "Cada liga es una frase. La biblioteca, un manuscrito."
              : "Each blend is a sentence. The library, a manuscript."}
          </p>
          <p className="text-tobacco/65 italic font-display text-[15px] leading-snug pt-1">
            {lang === "es"
              ? "“Distinguir, no impresionar.”"
              : "“To distinguish, not to impress.”"}
          </p>
        </>
      }
      cta={
        <ChapterCta
          testid="collection-cta"
          onClick={() => document.getElementById("archive")?.scrollIntoView({ behavior: "smooth" })}
        >
          {lang === "es" ? "Continuar al Archivo" : "Continue to The Archive"} →
        </ChapterCta>
      }
      illustration={
        <div className="w-full">
          {/* Cigar row */}
          <div className="grid grid-cols-4 gap-2 md:gap-4 items-end">
            {items.slice(0, 4).map((b, i) => (
              <button
                type="button"
                key={b.name}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => setActive(i)}
                data-testid={`blend-${i}`}
                className="flex flex-col items-center text-center group"
              >
                <CigarVertical
                  className={`h-[200px] md:h-[240px] w-auto transition-opacity duration-700 ${
                    active === i ? "opacity-100" : "opacity-55 group-hover:opacity-85"
                  }`}
                  label={b.name.split(" ")[0]}
                  smoke={active === i}
                />
                <div
                  className={`mt-3 font-mono text-[9px] tracking-widest2 uppercase transition-colors duration-700 ${
                    active === i ? "text-tobacco" : "text-tobacco/55"
                  }`}
                >
                  {b.name}
                </div>
                <div className="mt-0.5 font-mono text-[8.5px] tracking-widest2 uppercase text-tobacco/45">
                  {b.strength}
                </div>
              </button>
            ))}
          </div>

          {/* Active reading — quiet single line strip */}
          <AnimatePresence mode="wait">
            <motion.div
              key={cur.name}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-6 pt-4 border-t border-tobacco/15 grid grid-cols-12 gap-3 items-center text-[12.5px]"
            >
              <div className="col-span-12 md:col-span-3 font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55">
                N° {String(active + 1).padStart(2, "0")} · {cur.year}
              </div>
              <div className="col-span-6 md:col-span-3 text-tobacco/85">
                <span className="font-mono text-[8.5px] tracking-widest2 uppercase text-tobacco/55 mr-2">
                  Wrapper
                </span>
                {cur.wrapper}
              </div>
              <div className="col-span-6 md:col-span-3 text-tobacco/85">
                <span className="font-mono text-[8.5px] tracking-widest2 uppercase text-tobacco/55 mr-2">
                  Notes
                </span>
                {cur.notes}
              </div>
              <div
                className="col-span-12 md:col-span-3 flex items-center justify-end gap-1.5"
                data-testid={`blend-intensity-${active}`}
              >
                {Array.from({ length: 5 }).map((_, k) => (
                  <span
                    key={k}
                    className={`h-px w-7 ${k < (cur.intensity || 0) ? "bg-tobacco" : "bg-tobacco/20"}`}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      }
      meta={
        <ManuscriptNote
          label={lang === "es" ? "Catálogo" : "Catalogue"}
          items={[
            { k: lang === "es" ? "Ligas" : "Blends", v: String(items.length || 4) },
            { k: lang === "es" ? "Vitolas" : "Vitolas", v: lang === "es" ? "Robusto · Toro · Churchill" : "Robusto · Toro · Churchill" },
            { k: lang === "es" ? "Edición" : "Edition", v: lang === "es" ? "Sello Privado" : "Private Seal" },
            { k: lang === "es" ? "Producción" : "Production", v: lang === "es" ? "Limitada" : "Limited" },
          ]}
        />
      }
      bridge={
        <ChapterBridge
          left={
            lang === "es"
              ? "Las ligas pasan. La filosofía permanece."
              : "Blends pass. Philosophy remains."
          }
          right={lang === "es" ? "Capítulo VI · El Archivo" : "Chapter VI · The Archive"}
          testid="bridge-v-to-vi"
          onClick={() => document.getElementById("archive")?.scrollIntoView({ behavior: "smooth" })}
        />
      }
      bg="bg-pearl-deep"
    />
  );
}
